/**
 * URS-F358 — Tipo de pregunta `composite_scale`.
 * Varios sub-apartados, cada uno apuntando a una escala dermatológica del
 * catálogo (que aporta su imagen y sus niveles 0-N). El usuario marca un
 * valor por sub-apartado y se muestra la suma simple al final. Ej: OECS,
 * GAGS, PASI lineal, etc.
 *
 * Modelo de pregunta (campos extra):
 *   composite_items: [{ label: str, scale_id: str }]
 *   composite_total_label: str (default "Total")
 *
 * Payload de respuesta:
 *   {
 *     items: { "Eritema": "2", "Edema": "1", ... },
 *     observations: { "Eritema": "...", "Edema": "..." },
 *     _observations: "comentario global opcional"
 *   }
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Trash } from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { DermScaleRenderer } from './DermScaleRenderer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// -- helpers ----------------------------------------------------------------
// URS-F510: `scalesById` opcional; cuando se pasa, los items que
// referencian una escala con `response_mode === 'numeric'` se EXCLUYEN
// del total (el usuario pidio explicitamente que las numericas no
// sumen).
export function compositeScaleSum(items, answers, scalesById = null) {
  const a = (answers && typeof answers === 'object' && answers.items) || {};
  let total = 0;
  for (const it of (items || [])) {
    if (scalesById && it.scale_id) {
      const sc = scalesById[it.scale_id];
      if (sc && sc.response_mode === 'numeric') continue;
    }
    const v = parseInt(a[it.label], 10);
    if (!Number.isNaN(v)) total += v;
  }
  return total;
}

export function compositeScaleMissing(question, answers) {
  const items = question.composite_items || [];
  const a = (answers && typeof answers === 'object' && answers.items) || {};
  const missing = [];
  for (const it of items) {
    if (!a[it.label] || String(a[it.label]).trim() === '') {
      missing.push(it.label || '(sub-apartado)');
    }
  }
  return missing;
}

// -- Designer ---------------------------------------------------------------
export function CompositeScaleDesigner({ value, onChange, lang = 'es' }) {
  const [scales, setScales] = useState([]);
  const items = value.composite_items || [];
  const totalLabel = value.composite_total_label || 'Total';

  useEffect(() => {
    axios.get(`${API_URL}/api/config/derm-scales`, { withCredentials: true })
      .then(r => setScales(r.data || []))
      .catch(() => setScales([]));
  }, []);

  const updItem = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange({ composite_items: next });
  };
  const addItem = () => onChange({ composite_items: [...items, { label: '', scale_id: '' }] });
  const removeItem = (idx) => onChange({ composite_items: items.filter((_, i) => i !== idx) });

  return (
    <div className="space-y-2" data-testid="composite-scale-designer">
      <div className="space-y-1.5">
        {items.length === 0 && (
          <p className="text-[11px] italic text-stone-500">{lang === 'es' ? 'Sin sub-apartados todavía. Pulsa "Añadir" para empezar.' : 'No sub-items yet. Click "Add" to start.'}</p>
        )}
        {items.map((it, idx) => (
          <div key={idx} className="grid grid-cols-12 gap-2 items-center bg-white rounded-sm border border-stone-200 px-2 py-1.5" data-testid={`csd-item-${idx}`}>
            <Input
              value={it.label || ''}
              onChange={e => updItem(idx, { label: e.target.value })}
              placeholder={lang === 'es' ? 'Sub-apartado (ej. Eritema)' : 'Sub-item (e.g. Erythema)'}
              className="col-span-5 h-8 text-sm"
              data-testid={`csd-item-label-${idx}`}
            />
            <Select value={it.scale_id || ''} onValueChange={v => updItem(idx, { scale_id: v })}>
              <SelectTrigger className="col-span-6 h-8 text-sm" data-testid={`csd-item-scale-${idx}`}>
                <SelectValue placeholder={lang === 'es' ? 'Selecciona escala dermatológica' : 'Select dermatological scale'} />
              </SelectTrigger>
              <SelectContent>
                {scales.map(s => (
                  <SelectItem key={s.scale_id} value={s.scale_id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeItem(idx)}
              className="col-span-1 h-8 w-8 text-red-500 hover:bg-red-50"
              data-testid={`csd-item-remove-${idx}`}
            ><Trash size={13} /></Button>
          </div>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addItem}
          className="h-7 text-xs gap-1 mt-1"
          data-testid="csd-add-item"
        ><Plus size={12} />{lang === 'es' ? 'Añadir sub-apartado' : 'Add sub-item'}</Button>
      </div>
      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-stone-200">
        <div className="space-y-1">
          <Label className="text-[11px] text-stone-600">{lang === 'es' ? 'Etiqueta del total' : 'Total label'}</Label>
          <Input
            value={totalLabel}
            onChange={e => onChange({ composite_total_label: e.target.value })}
            placeholder={lang === 'es' ? 'Ej. Puntuación OECS' : 'E.g. OECS score'}
            className="h-8 text-sm"
            data-testid="csd-total-label"
          />
        </div>
      </div>
      {scales.length === 0 && (
        <p className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
          {lang === 'es' ? 'No hay escalas dermatológicas en el catálogo. Crea primero las escalas en Configuración → Escalas dermatológicas.' : 'No dermatological scales in catalogue yet. Create them in Settings → Dermatological scales first.'}
        </p>
      )}
    </div>
  );
}

// -- Renderer ---------------------------------------------------------------
export function CompositeScaleRenderer({ question, answers, onChange, readOnly = false, publicMode = false, lang = 'es', studyId, volunteerCode, currentVisitCode }) {
  const items = question.composite_items || [];
  const totalLabel = question.composite_total_label || (lang === 'es' ? 'Total' : 'Total');
  const cur = (answers && typeof answers === 'object') ? answers : {};
  const curItems = (cur.items && typeof cur.items === 'object') ? cur.items : {};
  const curObs = (cur.observations && typeof cur.observations === 'object') ? cur.observations : {};
  // URS-F510: necesitamos saber que sub-escalas son numericas para
  // excluirlas del total. Cargamos la lista completa una vez.
  const [scalesById, setScalesById] = useState({});
  useEffect(() => {
    if (publicMode) return; // en publico se usa /meta por scale, no tenemos lista
    axios.get(`${API_URL}/api/config/derm-scales`, { withCredentials: true })
      .then(r => {
        const map = {};
        for (const s of (r.data || [])) map[s.scale_id] = s;
        setScalesById(map);
      })
      .catch(() => {});
  }, [publicMode]);
  const total = compositeScaleSum(items, cur, scalesById);

  const setItem = (label, v) => onChange({ ...cur, items: { ...curItems, [label]: v }, observations: curObs });
  const setObs = (label, v) => onChange({ ...cur, items: curItems, observations: { ...curObs, [label]: v } });

  return (
    <div className="space-y-3" data-testid={`composite-scale-render-${question.question_id || 'q'}`}>
      {items.map((it, idx) => (
        <div key={idx} className="bg-white border border-stone-200 rounded-sm px-3 py-2.5 space-y-2" data-testid={`csr-item-${idx}`}>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-semibold text-stone-800">{it.label}</span>
            <span className="text-[10px] text-stone-400">{lang === 'es' ? 'Puntuación:' : 'Score:'} <span className="font-mono text-stone-700">{curItems[it.label] ?? '—'}</span></span>
          </div>
          {it.scale_id ? (
            <DermScaleRenderer
              scaleId={it.scale_id}
              value={curItems[it.label] || ''}
              onChange={v => setItem(it.label, v)}
              disabled={readOnly}
              publicMode={publicMode}
              testIdPrefix={`csr-${idx}`}
              studyId={studyId}
              volunteerCode={volunteerCode}
              currentVisitCode={currentVisitCode}
            />
          ) : (
            <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1">
              {lang === 'es' ? 'Sub-apartado sin escala asignada' : 'Sub-item without assigned scale'}
            </p>
          )}
          <div className="border-t border-stone-100 pt-1.5">
            <Label className="text-[10px] text-stone-500 uppercase tracking-wide">{lang === 'es' ? 'Observaciones' : 'Observations'}</Label>
            <Textarea
              value={curObs[it.label] || ''}
              onChange={e => setObs(it.label, e.target.value)}
              placeholder={lang === 'es' ? 'Notas clínicas (opcional)' : 'Clinical notes (optional)'}
              disabled={readOnly}
              className="bg-white min-h-[42px] text-sm"
              data-testid={`csr-obs-${idx}`}
            />
          </div>
        </div>
      ))}
      <div className="flex items-center justify-end gap-3 bg-violet-50 border border-violet-200 rounded-sm px-4 py-2.5" data-testid="composite-scale-total">
        <span className="text-sm font-semibold uppercase tracking-wide text-violet-700">{totalLabel}:</span>
        <span className="text-2xl font-bold text-violet-900 tabular-nums">{total}</span>
      </div>
    </div>
  );
}
